import React, {
    Children,
    createContext,
    forwardRef,
    useCallback,
    useContext,
    useId,
    useMemo,
    useState
} from 'react'
import PropTypes from 'prop-types'
import {
    Box,
    Flex,
    Text,
    RadioGroup,
    createSlotRecipeContext,
    useFieldContext
} from '@chakra-ui/react3'
import {colorSwatchesRecipe} from './index.recipe'
import {useColorVariation} from '../../../hooks'
import {useProducts} from '@salesforce/commerce-sdk-react'
import {useHistory, useLocation} from 'react-router-dom'
import useEffectEvent from '../../../hooks/use-effect-event'
import {useIntl} from 'react-intl'

const {withContext, useStyles, withRootProvider} = createSlotRecipeContext({
    recipe: colorSwatchesRecipe
})

const ColorSwatchItemImpl = forwardRef(
    ({disableUnavailableColors, isOrderable, swatch, onHoverChange, ...rest}, ref) => {
        // default to true when not available
        const orderable = isOrderable ?? swatch?.orderable ?? true
        const styles = useStyles()
        return (
            <RadioGroup.Item
                ref={ref}
                value={swatch.productId}
                disabled={disableUnavailableColors && !orderable}
                data-unavailable={!orderable ? '' : undefined}
                onMouseEnter={() => onHoverChange?.(swatch)}
                onMouseLeave={() => onHoverChange?.(null)}
                {...rest}
                // group class to use the group chakra selectors to style the selected and focused states on swatchOuter
                className={`group ${rest.className ?? ''}`}
            >
                <RadioGroup.ItemHiddenInput />
                <Box as="span" className="sr-only">
                    {swatch.name}
                </Box>
                <Box css={styles.swatchOuter} data-unavailable={!orderable ? '' : undefined}>
                    <Box css={styles.swatchInner}>
                        {swatch.image ? (
                            <Box
                                backgroundRepeat="no-repeat"
                                backgroundSize="cover"
                                backgroundColor={swatch.name}
                                backgroundImage={`url("${
                                    swatch.image.link ?? swatch.image.disBaseLink
                                }")`}
                                css={styles.swatchImage}
                            />
                        ) : (
                            <Text css={styles.variationText}>{swatch.name.charAt(0)}</Text>
                        )}
                    </Box>
                </Box>
            </RadioGroup.Item>
        )
    }
)
if (process.env.NODE_ENV !== 'production') {
    ColorSwatchItemImpl.displayName = 'ColorSwatchItem'
}
ColorSwatchItemImpl.propTypes = {
    disableUnavailableColors: PropTypes.bool,
    isOrderable: PropTypes.bool,
    swatch: PropTypes.shape({
        productId: PropTypes.string,
        name: PropTypes.string,
        value: PropTypes.string,
        image: PropTypes.shape({
            link: PropTypes.string,
            disBaseLink: PropTypes.string
        }),
        orderable: PropTypes.bool
    }),
    onHoverChange: PropTypes.func
}
const ColorSwatchItemView = withContext(ColorSwatchItemImpl, 'swatch')
export const ColorSwatchItem = withRootProvider(ColorSwatchItemView)

const HoveredSwatchContext = createContext(null)
const SetHoveredSwatchContext = createContext(null)
const SelectedProductIdContext = createContext(null)
const SelectedProductContext = createContext(null)
const DisplayedProductsContext = createContext(null)
const ProductShortNamePlacementContext = createContext('auto')
const SelectedColorVariationContext = createContext(null)
const OnChangeContext = createContext(null)
const DisableUnavailableColorsContext = createContext(null)
const CurrentProductContext = createContext(null)
const CurrentColorVariationContext = createContext(null)
const OrderableByProductIdContext = createContext(null)

const renderChildren = (children, props, fallback = null) => {
    const toRender = children || fallback
    if (typeof toRender === 'function') {
        return toRender(props)
    }
    return toRender || fallback
}

const mapRenderChildren = (list, children, mapFn, fallback) => {
    const Fallback = fallback
    if (typeof children === 'function') {
        return list.map(mapFn).map(children)
    }
    const child = children && Children.only(children)
    return list.map(mapFn).map((props) =>
        child ? (
            React.cloneElement(child, {
                ...props,
                // allow override
                ...child.props,
                // key must be different for each clone
                key: props.key
            })
        ) : (
            <Fallback key={props.key} {...props} />
        )
    )
}

const ColorSwatchesRootImpl = ({
    children,
    disableUnavailableColors,
    orderableByProductId,
    product,
    selectors,
    selectedProductId = product?.id,
    productShortNamePlacement = 'auto',
    onChange: _onChange
}) => {
    if (product && selectors) {
        throw new Error('Only one of product or selectors can be passed to ColorSwatchesRoot.')
    }

    // Material variants: master products specified as variants to each other via c_variationValuesProductSelector
    const alternativeMasterProductIds = useMemo(
        () => product?.c_variationValuesProductSelector?.map(({ID}) => ID),
        [product]
    )
    const {data: productMaterialVariants} = useProducts(
        {
            parameters: {
                ids: alternativeMasterProductIds,
                allImages: true,
                expand: ['variations', 'images'],
                select: '(data.(id,c_masterSubType,c_productShortName,name,variationAttributes.(**),variants.(**),imageGroups[?(@.viewType==swatch)].(**)))'
            }
        },
        {
            enabled: alternativeMasterProductIds?.length > 1
        }
    )

    const displayedProducts = useMemo(() => {
        // selectors are a list of material variant products
        if (selectors) {
            return selectors.filter((p) => p?.id)
        }
        // if we have loaded material variants from the product
        if (productMaterialVariants?.data?.length) {
            return productMaterialVariants.data
                .filter((p) => p?.id)
                .map((p) => ({
                    ...p,
                    // need to passthrough the setProductId for correct isProductPartOfSet logic
                    setProductId: product.setProductId
                }))
        }
        // otherwise use the product
        return product?.id ? [product] : []
    }, [selectors, product, productMaterialVariants])

    const [hoveredSwatch, setHoveredSwatch] = useState(null)

    const selectedProduct = useMemo(() => {
        if (!selectedProductId) {
            return displayedProducts?.[0]
        }
        const master = displayedProducts?.find(
            (p) =>
                p.id === selectedProductId ||
                p.variants?.some((variant) => variant.productId === selectedProductId)
        )
        const variant =
            master?.id === selectedProductId
                ? master
                : master?.variants?.find((v) => v.productId === selectedProductId)
        return {
            ...master,
            ...variant
        }
    }, [displayedProducts, selectedProductId])

    const selectedColorVariation = useColorVariation(selectedProduct)

    const location = useLocation()
    const history = useHistory()
    const onChange = useEffectEvent((swatch) => {
        if (swatch.href) {
            history.replace(swatch.href, location.state)
        }
        _onChange?.(swatch)
    })

    // handle products without color variation - mainly type=item
    if (!selectedColorVariation?.values?.length) {
        return null
    }

    return (
        <SetHoveredSwatchContext.Provider value={setHoveredSwatch}>
            <HoveredSwatchContext.Provider value={hoveredSwatch}>
                <SelectedProductIdContext.Provider value={selectedProductId}>
                    <SelectedProductContext.Provider value={selectedProduct}>
                        <DisplayedProductsContext.Provider value={displayedProducts}>
                            <ProductShortNamePlacementContext.Provider
                                value={productShortNamePlacement}
                            >
                                <SelectedColorVariationContext.Provider
                                    value={selectedColorVariation}
                                >
                                    <OnChangeContext.Provider value={onChange}>
                                        <DisableUnavailableColorsContext.Provider
                                            value={Boolean(disableUnavailableColors)}
                                        >
                                            <OrderableByProductIdContext.Provider
                                                value={orderableByProductId}
                                            >
                                                {renderChildren(
                                                    children,
                                                    {
                                                        hoveredSwatch,
                                                        setHoveredSwatch,
                                                        selectedProductId,
                                                        selectedProduct,
                                                        displayedProducts,
                                                        selectedColorVariation,
                                                        onChange
                                                    },
                                                    () => (
                                                        <Flex
                                                            flexDirection="column"
                                                            gap={
                                                                displayedProducts?.length > 1
                                                                    ? 'spacing-8'
                                                                    : 'spacing-6'
                                                            }
                                                        >
                                                            <Text
                                                                display="flex"
                                                                gap="spacing-4"
                                                                textStyle="heading.small"
                                                            >
                                                                <Text
                                                                    as="span"
                                                                    color="text.primary"
                                                                >
                                                                    <ColorSwatchesColorLabel as="span" />
                                                                    <span>:</span>
                                                                </Text>
                                                                <ColorSwatchesSelectedColorValue
                                                                    as="span"
                                                                    color="text.tertiary"
                                                                />
                                                            </Text>

                                                            <ColorSwatchesSwatchGroups />
                                                        </Flex>
                                                    )
                                                )}
                                            </OrderableByProductIdContext.Provider>
                                        </DisableUnavailableColorsContext.Provider>
                                    </OnChangeContext.Provider>
                                </SelectedColorVariationContext.Provider>
                            </ProductShortNamePlacementContext.Provider>
                        </DisplayedProductsContext.Provider>
                    </SelectedProductContext.Provider>
                </SelectedProductIdContext.Provider>
            </HoveredSwatchContext.Provider>
        </SetHoveredSwatchContext.Provider>
    )
}

if (process.env.NODE_ENV !== 'production') {
    ColorSwatchesRootImpl.displayName = 'ColorSwatchesRoot'
}
ColorSwatchesRootImpl.propTypes = {
    children: PropTypes.node,
    disableUnavailableColors: PropTypes.bool,
    orderableByProductId: PropTypes.object,
    product: PropTypes.object,
    selectors: PropTypes.array,
    selectedProductId: PropTypes.string,
    productShortNamePlacement: PropTypes.oneOf(['auto', 'only', 'before']),
    onChange: PropTypes.func
}
const ColorSwatchesRoot = withRootProvider(ColorSwatchesRootImpl)

const ColorSwatchesSelectedColorValueImpl = forwardRef(({children, ...props}, ref) => {
    const selectedColorVariation = useContext(SelectedColorVariationContext)
    const hoveredSwatch = useContext(HoveredSwatchContext)

    if (selectedColorVariation === null) {
        throw new Error(
            'ColorSwatchesSelectedColorValue must be used within ColorSwatchesRoot (ColorSwatches.Root)'
        )
    }

    return (
        <Text ref={ref} {...props}>
            {children || hoveredSwatch?.name || selectedColorVariation?.selectedValue?.name || ''}
        </Text>
    )
})
if (process.env.NODE_ENV !== 'production') {
    ColorSwatchesSelectedColorValueImpl.displayName = 'ColorSwatchesSelectedColorValue'
}
ColorSwatchesSelectedColorValueImpl.propTypes = {
    children: PropTypes.node
}
const ColorSwatchesSelectedColorValue = withContext(
    ColorSwatchesSelectedColorValueImpl,
    'selectedColorValue'
)

/**
 * Composes the color label from the product short name in the placement chosen by the client:
 * `before` puts the short name in front of the color label ("Stand Color"), `only` shows it
 * instead of the color label, `auto` shows it only when multiple products render and so need
 * telling apart. Without a short name the color label always stands alone.
 */
const resolveColorLabel = ({
    placement,
    productShortName,
    colorLabel,
    multipleProducts,
    formatMessage
}) => {
    switch (placement) {
        case 'before':
            if (!productShortName) return colorLabel
            return colorLabel
                ? formatMessage(
                      {
                          id: 'product_detail.color.with_product_short_name',
                          defaultMessage: '{productShortName} {colorLabel}'
                      },
                      {productShortName, colorLabel}
                  )
                : productShortName
        case 'only':
            return productShortName || colorLabel
        case 'auto':
            return multipleProducts && productShortName ? productShortName : colorLabel
        default:
            throw new Error(
                `Unknown productShortNamePlacement "${placement}" passed to ColorSwatchesRoot (ColorSwatches.Root).`
            )
    }
}

const ColorSwatchesColorLabelImpl = forwardRef(({children, ...props}, ref) => {
    const {formatMessage} = useIntl()
    const product = useContext(SelectedProductContext)
    const selectedColorVariation = useContext(SelectedColorVariationContext)
    const displayedProducts = useContext(DisplayedProductsContext)
    const placement = useContext(ProductShortNamePlacementContext)

    if (displayedProducts === null) {
        throw new Error(
            'ColorSwatchesColorLabel must be used within ColorSwatchesRoot (ColorSwatches.Root)'
        )
    }

    const label = useMemo(
        () =>
            resolveColorLabel({
                placement,
                productShortName: product?.c_productShortName,
                colorLabel: selectedColorVariation?.name || '',
                multipleProducts: displayedProducts?.length > 1,
                formatMessage
            }),
        [
            placement,
            product?.c_productShortName,
            selectedColorVariation?.name,
            displayedProducts?.length,
            formatMessage
        ]
    )

    return (
        <Text data-testid="color-swatches-color-label" ref={ref} {...props}>
            {children || label}
        </Text>
    )
})
if (process.env.NODE_ENV !== 'production') {
    ColorSwatchesColorLabelImpl.displayName = 'ColorSwatchesColorLabel'
}
ColorSwatchesColorLabelImpl.propTypes = {
    children: PropTypes.node
}
const ColorSwatchesColorLabel = withContext(ColorSwatchesColorLabelImpl, 'colorLabel')

const ColorSwatchesSwatchGroupsImpl = forwardRef(({children, ...props}, ref) => {
    const displayedProducts = useContext(DisplayedProductsContext)

    if (displayedProducts === null) {
        throw new Error(
            'ColorSwatchesSwatchGroups must be used within ColorSwatchesRoot (ColorSwatches.Root)'
        )
    }

    if (!children && !displayedProducts?.length) {
        return null
    }

    return (
        <Box ref={ref} {...props}>
            {mapRenderChildren(
                displayedProducts,
                children,
                (product) => ({
                    key: product.id,
                    product,
                    displayedProducts
                }),
                ColorSwatchesSwatchGroup
            )}
        </Box>
    )
})
if (process.env.NODE_ENV !== 'production') {
    ColorSwatchesSwatchGroupsImpl.displayName = 'ColorSwatchesSwatchGroups'
}
ColorSwatchesSwatchGroupsImpl.propTypes = {
    children: PropTypes.node
}
const ColorSwatchesSwatchGroups = withContext(ColorSwatchesSwatchGroupsImpl, 'swatchGroups')

const ColorSwatchesSwatchGroupImpl = forwardRef(({children, product, ...props}, ref) => {
    const id = useId()
    const displayedProducts = useContext(DisplayedProductsContext)
    const colorVariation = useColorVariation(product, true)
    const selectedProductId = useContext(SelectedProductIdContext)
    const onChange = useContext(OnChangeContext)
    const fieldContext = useFieldContext()

    if (displayedProducts === null) {
        throw new Error(
            'ColorSwatchesSwatchGroup must be used within ColorSwatchesRoot (ColorSwatches.Root)'
        )
    }

    const onValueChange = useCallback(
        ({value: productId}) => {
            onChange?.(colorVariation?.values.find((v) => v.productId === productId) ?? {productId})
        },
        [onChange, colorVariation?.values]
    )

    if (!colorVariation?.values?.length) return null

    return (
        <CurrentProductContext.Provider value={product}>
            <CurrentColorVariationContext.Provider value={colorVariation}>
                <RadioGroup.Root
                    name={id}
                    value={selectedProductId ?? ''}
                    onValueChange={onValueChange}
                    unstyled
                    ref={ref}
                    aria-label={colorVariation?.name}
                    aria-describedby={
                        fieldContext?.invalid ? fieldContext.ids.errorText : undefined
                    }
                    {...props}
                >
                    {renderChildren(
                        children,
                        {product, colorVariation},
                        <>
                            <ColorSwatchesGroupLabel
                                as="legend"
                                srOnly={displayedProducts.length === 1}
                            />
                            <ColorSwatchesRow />
                        </>
                    )}
                </RadioGroup.Root>
            </CurrentColorVariationContext.Provider>
        </CurrentProductContext.Provider>
    )
})
if (process.env.NODE_ENV !== 'production') {
    ColorSwatchesSwatchGroupImpl.displayName = 'ColorSwatchesSwatchGroup'
}
ColorSwatchesSwatchGroupImpl.propTypes = {
    children: PropTypes.node,
    product: PropTypes.object,
    onChange: PropTypes.func
}
const ColorSwatchesSwatchGroup = withContext(ColorSwatchesSwatchGroupImpl, 'swatchGroup')

const ColorSwatchesGroupLabelImpl = forwardRef(({children, ...props}, ref) => {
    const displayedProducts = useContext(DisplayedProductsContext)
    const product = useContext(CurrentProductContext)
    const colorVariation = useContext(CurrentColorVariationContext)

    if (product === null || displayedProducts === null) {
        throw new Error(
            'ColorSwatchesGroupLabel must be used within ColorSwatchesSwatchGroup (ColorSwatches.SwatchGroup)'
        )
    }

    return (
        <Text ref={ref} {...props}>
            {renderChildren(
                children,
                {product, colorVariation},
                (displayedProducts?.length > 1 && product.c_masterSubType) ||
                    colorVariation.name ||
                    ''
            )}
        </Text>
    )
})
if (process.env.NODE_ENV !== 'production') {
    ColorSwatchesGroupLabelImpl.displayName = 'ColorSwatchesGroupLabel'
}
ColorSwatchesGroupLabelImpl.propTypes = {
    children: PropTypes.node
}
const ColorSwatchesGroupLabel = withContext(ColorSwatchesGroupLabelImpl, 'groupLabel')

const ColorSwatchesRowImpl = forwardRef(({children, ...props}, ref) => {
    const colorVariation = useContext(CurrentColorVariationContext)
    const disableUnavailableColors = useContext(DisableUnavailableColorsContext)
    const onHoverChange = useContext(SetHoveredSwatchContext)
    const orderableByProductId = useContext(OrderableByProductIdContext)

    if (colorVariation === null) {
        throw new Error(
            'ColorSwatchesRow must be used within ColorSwatchesSwatchGroup (ColorSwatches.SwatchGroup)'
        )
    }

    return (
        <Box ref={ref} {...props}>
            {mapRenderChildren(
                colorVariation.values,
                children,
                (swatch) => ({
                    disableUnavailableColors,
                    isOrderable: orderableByProductId?.[swatch.productId],
                    onHoverChange,
                    swatch
                }),
                ColorSwatchItemView
            )}
        </Box>
    )
})
if (process.env.NODE_ENV !== 'production') {
    ColorSwatchesRowImpl.displayName = 'ColorSwatchesRow'
}
ColorSwatchesRowImpl.propTypes = {
    children: PropTypes.node
}
const ColorSwatchesRow = withContext(ColorSwatchesRowImpl, 'row')

const ColorSwatches = {
    Root: ColorSwatchesRoot,
    ColorLabel: ColorSwatchesColorLabel,
    SelectedColorValue: ColorSwatchesSelectedColorValue,
    SwatchGroups: ColorSwatchesSwatchGroups,
    SwatchGroup: ColorSwatchesSwatchGroup,
    SwatchGroupLabel: ColorSwatchesGroupLabel,
    Row: ColorSwatchesRow
}

export default ColorSwatches
